/*
 * Author: Ben Zhao
 * File Created: Tuesday, 25-06-17 10:10 am
 * Last Modified: Tuesday, 25-07-22 07:45 pm, by Dw
 */

import { useCallback, useEffect, useMemo } from 'react';

import { useRouter } from 'next/router';

import dayjs from 'dayjs';
import { create } from 'zustand';

import { isOpenApi } from '@ucard/next-core/utils';

import { showDialog, showStatusDialog } from '@ucard/ds/message';

import { createStoreActions, fetchImage, generateUseShallow } from '@web/utils';

import type { ImageNoticeProps } from '../components/ImageNotiice';
import ImageNotice from '../components/ImageNotiice';
import useLanguage from '../hooks/useLanguage';
import type { NotificationItem } from '../services/notification';
import { getNotifications } from '../services/notification';
import { useUserInfo } from './useUserStore';

interface IState {
  notifications: NotificationItem[];
  isFetching: boolean;
}

const defaultState: IState = {
  notifications: [],
  isFetching: true,
};

interface IAction {
  fetchNotifications: (_p: {
    locale: string;
    bizType?: string;
  }) => Promise<void>;
}

const NOTIFICATION_STORE_KEY = 'dnks';

const getDismissedModals = (): Record<
  string,
  { ignoreDays: number; closedAt: number }
> => {
  try {
    return (
      JSON.parse(localStorage.getItem(NOTIFICATION_STORE_KEY) || '{}') || {}
    );
  } catch {
    return {};
  }
};

const saveDismissedModal = (id: string, ignoreDays = -1) => {
  const map = getDismissedModals();

  map[id] = {
    ignoreDays,
    closedAt: Date.now(),
  };

  localStorage.setItem(NOTIFICATION_STORE_KEY, JSON.stringify(map));
};

const actions = createStoreActions<IState, IAction>((set) => ({
  fetchNotifications: async (params) => {
    set({ isFetching: true });
    try {
      const { data } = await getNotifications(params);

      set({ notifications: data, isFetching: false });
    } catch (err) {
      console.error('fetchNotifications failed', err);

      set({ isFetching: false });
    }
  },
}));

const useNotificationsStore = create<IAction & IState>((set, get) => ({
  ...defaultState,
  ...actions(set, get),
}));

export const useNotifications = generateUseShallow<IState, IAction>(
  useNotificationsStore,
);

export const useNotificationsFilter = (params?: {
  bizType?: 'GLOBAL' | 'DVAULT_HOME';
  type?: 'MODAL' | 'DVAULT_BANNER' | 'MODAL_TEXT' | 'MODAL_IMAGE';
}) => {
  const { notifications } = useNotifications();

  const ns = useMemo(() => {
    if (!params?.bizType && !params?.type) {
      return notifications;
    }

    let tmp = notifications;

    if (params.bizType) {
      tmp = tmp.filter((item) => item.bizType === params.bizType);
    }

    if (params.type) {
      tmp = tmp.filter((item) => item.type === params.type);
    }

    return tmp;
  }, [params?.bizType, params?.type, notifications]);

  return ns;
};

export const useNotificationFetch = (bizType: string) => {
  const { isFetching } = useNotifications(['isFetching']);
  const router = useRouter();

  const showModelDialog = useCallback(async (item: NotificationItem) => {
    await new Promise((resolve) => {
      const close = showStatusDialog({
        header: item.title,
        content: item.content,
        btn1: {
          label: item.btn,
          onClick: async () => {
            saveDismissedModal(item.id, item.ignoreDays ?? -1);

            if (item.webRedirectUrl) {
              if (item.webRedirectUrl.startsWith('/')) {
                // 如果是内部链接
                await router.push(item.webRedirectUrl);
              } else {
                window.location.href = item.webRedirectUrl;
              }
            }

            close();
            resolve(true);
          },
        },
        onClose: () => {
          resolve(true);
        },
        showClose: Boolean(item.showCloseBtn),
        disableBackdropClose: true,
      });
    });
  }, []);

  const showImageDialog = useCallback(async (item: NotificationItem) => {
    await new Promise((resolve) => {
      const close = showDialog<ImageNoticeProps>(ImageNotice, {
        disableBackdropClose: true,
        onClick: async () => {
          saveDismissedModal(item.id, item.ignoreDays ?? -1);

          if (item.webRedirectUrl) {
            if (item.webRedirectUrl.startsWith('/')) {
              // 如果是内部链接
              await router.push(item.webRedirectUrl);
            } else {
              window.location.href = item.webRedirectUrl;
            }
          }

          close();
          resolve(true);
        },
        onClose: () => {
          resolve(true);
        },
        showClose: Boolean(item.showCloseBtn),
        data: item,
      });
    });
  }, []);

  const showModals = async () => {
    const { notifications } = useNotificationsStore.getState();

    const dismissedMap = getDismissedModals();

    const modals = notifications.filter((item) => {
      if (item.bizType !== bizType) return false;

      if (!['MODAL', 'MODAL_TEXT', 'MODAL_IMAGE'].includes(item.type))
        return false;

      const record = dismissedMap[item.id];

      if (!record) return true;

      if (record.ignoreDays === -1) return false; // 永不弹

      if (record.ignoreDays === 0) return true; // 每次都弹

      // 跨自然日逻辑
      const closedDay = dayjs(record.closedAt).startOf('day');
      const today = dayjs().startOf('day');

      const diffDays = today.diff(closedDay, 'day');

      return diffDays >= record.ignoreDays;
    });

    if (!modals.length) {
      return true;
    }

    for (const item of modals) {
      // 如果是 img 图片请求成功之后再展示
      if (item.type === 'MODAL_IMAGE' && item.imageUrl) {
        const ok = await fetchImage(item.imageUrl);

        if (!ok) {
          console.warn(`Failed to fetch image for notification: ${item.id}`);
          continue;
        }
      }

      if (item.type === 'MODAL_IMAGE') {
        await showImageDialog(item);
      } else {
        await showModelDialog(item);
      }
    }
  };

  return {
    loading: isFetching,
    fn: showModals,
  };
};

// hack
const IGNORE_MODAL_PATHNAME = [
  '/landing/home',
  '/verification/manual',
  '/verification/singpass',
];

export const useNotificationEstablish = () => {
  const { fetchNotifications } = useNotifications(['fetchNotifications']);

  const { isLogin } = useUserInfo();
  const { curLocale } = useLanguage();
  const router = useRouter();

  useEffect(() => {
    // 不能是 openAPI
    if (
      !isOpenApi &&
      !IGNORE_MODAL_PATHNAME.includes(router.pathname) &&
      isLogin
    ) {
      fetchNotifications({
        locale: curLocale,
      }).catch((err) => {
        console.error('fetchNotifications failed', err);
      });
    } else if (isLogin === false) {
      useNotificationsStore.setState({ isFetching: false });
    }
  }, [isLogin, curLocale, router.pathname]);
};
